import axios from 'axios';
import api from "../config/axios"; // Tu instancia configurada de Axios
import API_ROUTES from "../config/apiRoutes"; // Tu archivo de rutas

// --- Importamos los tipos necesarios y la interfaz de respuesta ---
import { ApiResponse } from '../interfaces/api.types';
import { 
    LoginCredentials, 
    AuthResponseData, 
    RefreshTokenPayload,
    RequestResetPayload,
    ResetPasswordPayload
} from '../interfaces/auth.types';

/**
 * @function handleApiError
 * @description Función de ayuda para manejar errores de Axios de forma consistente.
 * Lanza un error con el mensaje del backend para que el UI pueda mostrarlo.
 * @param {unknown} error - El error capturado en el bloque catch.
 * @returns {never} - Esta función nunca devuelve un valor, siempre lanza un error.
 */
const handleApiError = (error: unknown): never => {
    if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data.message || 'Ocurrió un error en el servidor.');
    }
    throw new Error('Ocurrió un error de conexión o un problema desconocido.');
};

/**
 * @function login
 * @description Envía las credenciales del usuario para iniciar sesión.
 * @param {LoginCredentials} credentials - Objeto con username y password.
 * @returns {Promise<AuthResponseData>} Una promesa que resuelve a los datos de la sesión.
 */
export const login = async (credentials: LoginCredentials): Promise<AuthResponseData> => {
    try {
        const response = await api.post<ApiResponse<AuthResponseData>>(API_ROUTES.auth.login, credentials);
        
        if (!response.data.success || !response.data.data) {
            throw new Error(response.data.message || "La respuesta del login no fue exitosa.");
        }

        // Persistir la sesión en localStorage
        const { user, accessToken, refreshToken } = response.data.data;
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);

        return response.data.data;
    } catch (error) {
        // CORRECCIÓN: Al lanzar el error aquí, aseguramos que la función siempre
        // o devuelve un valor (en el try) o lanza un error (en el catch).
        throw handleApiError(error);
    }
};

/**
 * @function logout
 * @description Limpia la sesión del lado del cliente e invalida el token en el backend.
 */
export const logout = async (payload: RefreshTokenPayload): Promise<void> => {
    try {
        await api.post<ApiResponse<null>>(API_ROUTES.auth.logout, payload);
    } catch (error) {
        console.error("Error al notificar el logout al backend:", error);
    } finally {
        // Aseguramos la limpieza del storage local independientemente del resultado del backend.
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
    }
};

/**
 * @function refreshToken
 * @description Solicita un nuevo accessToken usando un refreshToken válido.
 * @param {RefreshTokenPayload} payload - Objeto que contiene el refreshToken.
 * @returns {Promise<{ accessToken: string, refreshToken: string }>} Los nuevos tokens.
 */
export const refreshToken = async (payload: RefreshTokenPayload): Promise<{ accessToken: string, refreshToken: string }> => {
    try {
        const response = await api.post<ApiResponse<{ accessToken: string, refreshToken: string }>>(API_ROUTES.auth.refreshToken, payload);
        
        if (!response.data.success || !response.data.data) {
            throw new Error(response.data.message || "No se pudo refrescar el token.");
        }
        
        const { accessToken, refreshToken: newRefreshToken } = response.data.data;
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        
        return response.data.data;
    } catch (error) {
        throw handleApiError(error);
    }
};

/**
 * @function requestPasswordReset
 * @description Inicia el proceso de reseteo de contraseña.
 * @param {RequestResetPayload} payload - Objeto que contiene el email.
 * @returns {Promise<string>} Un mensaje de confirmación del backend.
 */
export const requestPasswordReset = async (payload: RequestResetPayload): Promise<string> => {
    try {
        const response = await api.post<ApiResponse<null>>(API_ROUTES.auth.requestReset, payload);
        return response.data.message || 'Solicitud enviada. Revisa tu correo electrónico.';
    } catch (error) {
        throw handleApiError(error);
    }
};

/**
 * @function resetPassword
 * @description Finaliza el proceso de reseteo de contraseña.
 * @param {ResetPasswordPayload} payload - Objeto con token y contraseñas.
 * @returns {Promise<string>} Un mensaje de éxito del backend.
 */
export const resetPassword = async (payload: ResetPasswordPayload): Promise<string> => {
    try {
        const response = await api.post<ApiResponse<null>>(API_ROUTES.auth.resetPassword, payload);
        return response.data.message || 'Contraseña actualizada exitosamente.';
    } catch (error) {
        throw handleApiError(error);
    }
};